import React, { useRef } from 'react';
import { ContentMetricType } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import BeaconProChart from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/BeaconProChart';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import DonutChartLoading from 'src/components/Charts/Donut/DonutChartLoading';
import { useStacklineTheme } from '@stackline/ui';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import CircularProgressPercentage from 'src/components/common/UploadAnimation/CircularProgressPercentage';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import { ChartDataAdapter } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';
import { KeyMetricsData } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/KeyMetricsAdapter';
import { GenericChartLoading } from 'src/components/common/Loading/PlaceHolderLoading/PlaceHolderLoading';
import { useProductEntity, useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import ReactDOMServer from 'react-dom/server';
import { getRetailerScores } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/utils';
import { saveBarChartAsDelimitedData } from 'src/utils/chartUtils';
import saveAs from 'file-saver';

interface ContentDonutAndMiniTopEntityChartProps {
  title: string;
  queryFn: () => Promise<any>;
  /**
   * Key used to determine subtitle content and queryKey uniqueness
   */
  type: ContentMetricType;
}

export const ContentAccuracyTopSection = ({ title, queryFn, type }: ContentDonutAndMiniTopEntityChartProps) => {
  const formatMetric = useMetricFormatter();
  const theme = useStacklineTheme();
  const primaryLegendLabel = useAppSelector((state) => state.mainTimePeriod.displayName);
  const retailerId = useRetailerId();
  const { isProductEntity } = useProductEntity();
  const fullChartRef = useRef<HTMLDivElement>(null);

  //   dynamically render topEntity categories based on the retailerId
  const dynamicTopEntityRenderer = (data: [string, number][]) => {
    const scoreList = getRetailerScores(Number(retailerId));
    return data?.filter((datum) => scoreList.includes(datum[0]));
  };

  return (
    <div key={title} ref={fullChartRef}>
      <BeaconProChart queryFn={queryFn} name={`content-donut-topEntity-chart-${title.toLocaleLowerCase()}-${type}`}>
        <BeaconProChart.Header
          menuButton={<></>}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <Text variant="h4">{title}</Text>
          <CommonSummaryInfoSubtitle title="" includeSubtitle />
        </BeaconProChart.Header>

        <BeaconProChart.Visualization
          renderLoading={() => (
            <div
              style={{
                marginTop: '32px',
                width: '318px',
                height: '321px',
                justifyContent: 'space-between',
                display: 'flex'
              }}
            >
              <div style={{ marginTop: '8px' }}>
                <DonutChartLoading />
              </div>
              <div style={{ minWidth: '600px', marginLeft: '120px', height: '1000px' }}>
                <GenericChartLoading />
              </div>
            </div>
          )}
          render={(data: ChartDataAdapter<KeyMetricsData>) => {
            const { topEntityPrimaryData, topEntitySecondaryData, primaryOverallScore, secondaryOverallScore } =
              data.transform();

            const filteredPrimaryData = dynamicTopEntityRenderer(topEntityPrimaryData).filter(
              (datum) => datum[0] !== 'Enhanced Score'
            );
            const filteredSecondaryData = dynamicTopEntityRenderer(topEntitySecondaryData).filter(
              (datum) => datum[0] !== 'Enhanced Score'
            );

            return (
              <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ marginTop: '8px' }}>
                  <CircularProgressPercentage
                    percentage={primaryOverallScore}
                    customLabel={
                      <div
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: '18px' }}
                      >
                        <Text
                          variant="h4"
                          sx={{
                            fontSize: '48px'
                          }}
                        >
                          {(primaryOverallScore * 100).toFixed(1)}
                        </Text>
                        <Text variant="h4" sx={{ marginLeft: '8px' }}>
                          %
                        </Text>
                      </div>
                    }
                  />
                </div>

                <div style={{ minWidth: '600px' }}>
                  <BeaconChartWithLegend
                    saveImageRef={fullChartRef}
                    showTitleSummary={false}
                    convertSeriesToDelimitedData={async () => {
                      if (isProductEntity) {
                        const csvContent = [
                          ['Entity', primaryLegendLabel],
                          ['Overall Score', primaryOverallScore],
                          ...filteredPrimaryData
                        ]
                          .map((rowElements) =>
                            rowElements.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')
                          )
                          .join('\n');
                        const blob = new Blob([csvContent], { type: 'text/plain;charset=utf-8' });
                        saveAs(blob, `content accuracy score.csv`);
                        return Promise.resolve();
                      } else {
                        await saveBarChartAsDelimitedData(
                          [['Overall Score', primaryOverallScore], ...filteredPrimaryData],
                          [['Overall Score', secondaryOverallScore], ...filteredSecondaryData],
                          { fileTitle: 'content accuracy score.csv' }
                        );

                        return Promise.resolve();
                      }
                    }}
                    title="Content Score"
                  >
                    <TopEntityChartV2
                      xAxisFormatter={(xAxisCategory) => {
                        return ReactDOMServer.renderToStaticMarkup(
                          <GenericXAxisLabel
                            xAxisCategory={xAxisCategory}
                            wrapperStyle={{ width: '55px', wordBreak: 'keep-all', overflow: 'visible' }}
                          />
                        );
                      }}
                      chartSeries={[
                        ...(!isProductEntity
                          ? [
                              {
                                type: 'column' as const,
                                data: filteredSecondaryData,
                                metricType: METRICTYPE.PERCENT,
                                color: theme.colors.casper
                              }
                            ]
                          : []),

                        {
                          type: 'column' as const,
                          data: filteredPrimaryData,
                          metricType: METRICTYPE.PERCENT,
                          color: theme.colors.primary
                        }
                      ]}
                      seriesColors={
                        isProductEntity ? [theme.colors.primary] : [theme.colors.casper, theme.colors.primary]
                      }
                      chartOptionsOverride={(defaults) => ({
                        ...defaults,
                        plotOptions: {
                          ...defaults.plotOptions,
                          series: {
                            ...defaults?.series?.plotOptions,
                            dataLabels: {
                              ...defaults.plotOptions?.series?.dataLabels,
                              formatter() {
                                return ` ${formatMetric(this.y, METRICTYPE.PERCENT, {
                                  decimalPlaces: 1,
                                  showFullValue: false
                                })}`;
                              }
                            }
                          }
                        },
                        chart: {
                          ...defaults.chart,
                          height: 240,
                          spacingTop: 5,
                          marginBottom: 39,
                          spacingBottom: 0
                        }
                      })}
                      leftAlignBars={false}
                    />
                  </BeaconChartWithLegend>
                </div>
              </div>
            );
          }}
        ></BeaconProChart.Visualization>
      </BeaconProChart>
    </div>
  );
};
